/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DropTarget } from 'ag-grid-community';
import { Observable } from 'rxjs';

/**
 * Interface which should be implemented by all <code>DropTarget</code>s.
 * Each <code>DropTarget</code> belongs to specific DnD implementation.
 *
 * All implementation details are VERY dependent on DnD implementation.
 * For example, for ag-grid component all <code>DropTarget</code>s MUST implement internal ag-grid interface.
 */
export interface DndDropTarget {
  engine: string;

  /**
   * This callback is triggered when DnD operation has started
   */
  onDragStart(event: DndDragEvent): void;

  /**
   * This callback is triggered when DnD operation has finished
   */
  onDragStop(event: DndDragEvent): void;
}

/**
 * Subscription provides set of <code>DropTarget</code>-related events.
 */
export interface DndDropTargetSubscription {
  /**
   * Emits new event each time when set of targets has changed
   */
  targets: Observable<DndDropTarget[]>;
  /**
   * Emits new event when new <code>DropTarget</code> has been added
   */
  targetAdded: Observable<DndDropTarget>;
  /**
   * Emits new event when existing <code>DropTarget</code> has been removed
   */
  targetRemoved: Observable<DndDropTarget>;
}

/**
 * Implementation of this interface is created by <code>DndManager</code>.
 * Component providing DnD implementation is resposible for calling <code>startDrag</code> and <code>stopDrag</code>
 * methods when dragging is started/stopped.
 * Each <code>DragSource</code> belongs to specific <code>selector</code> and <code>engine</code>.
 */
export interface DndDragSource {
  selector: string;
  engine: string;

  /**
   * Notifies all listening target that dragging has been started.
   */
  startDrag(element: any): void;

  /**
   * Notifies all listening target that dragging has been stopped.
   */
  stopDrag(): void;
}

/**
 * Interface for start and stop dragging events
 */
export interface DndDragEvent {
  type: 'start' | 'stop';
  source: DndDragSource;
  element: any;
}

/**
 * Subscription provides set of <code>DragSource</code>-related events.
 */
export interface DndDragSourceSubscription {
  /**
   * Emits new event each time when set of targets has changed
   */
  sources: Observable<DndDragSource[]>;
  /**
   * Emits new event when new <code>DragSource</code> has been added
   */
  sourceAdded: Observable<DndDragSource>;
  /**
   * Emits new event when new <code>DragSource</code> has been removed
   */
  sourceRemoved: Observable<DndDragSource>;
}

export const DND_GRID_ENGINE = 'ag-grid-community';

/**
 * <code>DropTarget</code> interface specific for ag-grid component
 */
export interface GridDndDropTarget extends DndDropTarget {
  engine: 'ag-grid-community';
  gridDropTarget: DropTarget;
}
